import React, { useState, useMemo } from "react";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import { Button } from "../../ui/button";
import { 
  CopyIcon,
  FlaskConicalIcon
} from "lucide-react";
import AiStars from "../../../assets/svg/ai-creation.svg?react";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { RequestViewButton } from "../../shared/button/Buttons";
import DatasetRequestEdit from "./DatasetRequestEdit";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../../ui/paginated-table";
import moment from "moment";
import { cn } from "../../../lib/utils";

interface DatasetAddedRequestsTableProps {
  // Data
  requests: ChatCompletionRequest[];
  totalRequests: number;
  searchResults: ChatCompletionRequest[];
  
  // State
  currentPage: number;
  pageSize: number;
  loading: boolean;
  isSearchMode: boolean;
  isSearchLoading: boolean;
  
  // Search state
  searchType: string;
  searchValue: string;
  responseKeywords: string[];
  responseKeywordInput: string;
  
  // Selection
  selectedRequests: ChatCompletionRequest[];
  showOnlySelected: boolean;
  
  // Request type filters
  requestTypeFilters: {
    SYNTHETIC: boolean;
    PROD: boolean;
    RERUN: boolean;
  };
  
  // Handlers
  onPageChange: (page: number) => void;
  onSearch: () => void;
  onClearSearch: () => void;
  onSearchTypeChange: (type: string) => void;
  onSearchValueChange: (value: string) => void;
  onResponseKeywordsChange: (keywords: string[]) => void;
  onResponseKeywordInputChange: (input: string) => void;
  onSelectedRequestsChange: (requests: ChatCompletionRequest[]) => void;
  onShowOnlySelectedChange: (show: boolean) => void;
  onRequestTypeFiltersChange: (filters: any) => void;
  onRemoveSelectedRequests: () => void;
  onViewRequest: (request: ChatCompletionRequest) => void;
  onAugmentRequest: (request: ChatCompletionRequest) => void;
  onRequestSave: (request: ChatCompletionRequest) => void;
  
  // Props
  datasetId: string | undefined;
  apiKey: ApiKeyOrGetter;
}

const DatasetAddedRequestsTable: React.FC<DatasetAddedRequestsTableProps> = ({
  requests,
  totalRequests,
  searchResults,
  currentPage,
  pageSize,
  loading,
  isSearchMode,
  isSearchLoading,
  searchType,
  searchValue,
  responseKeywords,
  responseKeywordInput,
  selectedRequests,
  showOnlySelected,
  requestTypeFilters,
  onPageChange,
  onSearch,
  onClearSearch,
  onSearchTypeChange,
  onSearchValueChange,
  onResponseKeywordsChange,
  onResponseKeywordInputChange,
  onSelectedRequestsChange,
  onShowOnlySelectedChange,
  onRequestTypeFiltersChange,
  onRemoveSelectedRequests,
  onViewRequest,
  onAugmentRequest,
  onRequestSave,
  datasetId,
  apiKey
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig<ChatCompletionRequest>>({ key: null, direction: "desc" });

  const handleSort = (key: keyof ChatCompletionRequest) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const displayData = useMemo(() => {
    return showOnlySelected ? selectedRequests : requests;
  }, [requests, selectedRequests, showOnlySelected]);

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getRequestTypeColor = (type: string) => {
    switch (type) {
      case "SYNTHETIC":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "PROD":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "RERUN":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case "SYNTHETIC":
        return "Augmented";
      case "PROD":
        return "Production";
      case "RERUN":
        return "Rerun";
      default:
        return "Other";
    }
  };

  const columns: PaginatedTableColumn<ChatCompletionRequest>[] = [
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[20%]",
      sortable: true,
      render: (request) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(request.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(request.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "request_type",
      header: "Type",
      width: "w-[10%]",
      render: (request) => (
        <span className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getRequestTypeColor(request.request_type || "")
        )}>
          {getRequestTypeLabel(request.request_type || "")}
        </span>
      )
    },
    {
      key: "metadata",
      header: (
        <div className="flex items-center gap-1">
          Tags
          <AiStars className="w-3 h-3 text-muted-foreground" />
        </div>
      ),
      width: "w-[50%]",
      render: (request) => (
        <div className="flex flex-wrap gap-1">
          {(() => {
            try {
              // First check for dataset-specific tags
              let tags: string[] = [];
              if (request.metadata?._maitai_dataset_request_meta) {
                const meta = JSON.parse(
                  request.metadata._maitai_dataset_request_meta as string
                );
                tags = meta.tags || [];
              }

              // If no dataset-specific tags, fall back to request metadata tags
              if (tags.length === 0 && request.metadata?._maitai_request_meta) {
                const metaData = JSON.parse(
                  request.metadata._maitai_request_meta as string
                );
                tags = metaData.tags || [];
              }

              const displayTags = tags.slice(0, 2);
              const hasMoreTags = tags.length > 2;
              
              return (
                <>
                  {displayTags.map((tag: string) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                    >
                      {tag}
                    </span>
                  ))}
                  {hasMoreTags && (
                    <span className="text-xs text-muted-foreground">
                      +{tags.length - 2} more
                    </span>
                  )}
                  {tags.length === 0 && (
                    <span className="text-sm text-muted-foreground">No tags</span>
                  )}
                </>
              );
            } catch (e) {
              console.error("Error parsing metadata:", e);
              return <span className="text-sm text-muted-foreground">No tags</span>;
            }
          })()}
        </div>
      )
    },
    {
      key: "request_id",
      header: "Request ID",
      width: "w-[15%]",
      sortable: true,
      render: (request) => (
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
            ...{request.request_id?.slice(-8) || ""}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(request.request_id || "");
            }}
          >
            <CopyIcon className="w-3 h-3" />
          </Button>
        </div>
      )
    },
    {
      key: "request_id", // Using request_id as key since there's no actions field
      header: "Actions",
      width: "w-[15%]",
      render: (request) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() => onAugmentRequest(request)}
          >
            <FlaskConicalIcon className="w-3 h-3" />
            Augment
          </Button>
          <RequestViewButton
            apiKey={apiKey}
            request={request}
            onClick={() => onViewRequest(request)}
            isEditable={false}
            onUpdated={onRequestSave}
          >
            View
          </RequestViewButton>
          <DatasetRequestEdit
            request={request}
            onSave={onRequestSave}
            datasetId={datasetId}
            apiKey={apiKey}
          />
        </div>
      )
    }
  ];

  const totalItems = showOnlySelected 
    ? selectedRequests.length 
    : isSearchMode 
      ? searchResults.length 
      : totalRequests;

  return (
    <PaginatedTable
      data={displayData}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={handleSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={totalItems}
      emptyTitle="No requests found"
      emptyDescription="Add requests to this dataset to get started."
      rowClassName="cursor-pointer hover:bg-accent/20 transition-colors"
      serverSidePagination={true}
      loadingRows={5}
    />
  );
};

export default DatasetAddedRequestsTable; 